/**
 * Courbe d'une métrique de vente, superposée à sa référence.
 *
 * Deux traits sur le MÊME axe — ils mesurent la même chose, sur deux
 * fenêtres : la période en plein (emplacement 1), la référence en pointillé
 * (emplacement 2). Le pointillé dit « ceci est un repère », pas une seconde
 * série à lire pour elle-même.
 *
 * Alignement par RANG (cf. `compareSeries`) : l'axe porte les dates de la
 * période affichée, et l'infobulle rappelle la date correspondante de la
 * référence, sans quoi « l'an dernier » resterait abstrait.
 */

import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { formatDayLong, formatDayShort } from '../lib/format';
import { bucketSizeFor, compareSeries, relativeChange, type ComparedPoint } from '../lib/sales';
import { AXIS_STYLE, COLORS, LINE_WIDTH } from '../lib/theme';
import type { DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartLegend } from './ChartTooltip';
import { useChartMarkers } from '../hooks/useChartMarkers';
import { MarkerStrip } from './MarkerStrip';

interface SalesTrendChartProps<T extends { date: string }> {
  title: string;
  subtitle: string;
  current: readonly T[];
  reference: readonly T[];
  /** Nom de la référence dans la légende, ex. « Même période l'an dernier ». */
  referenceLabel: string;
  value: (point: T) => number;
  formatValue: (value: number) => string;
  formatAxis: (value: number) => string;
  /** Flux (défaut) ou stock — cf. `compareSeries`. */
  aggregate?: 'sum' | 'last';
  className?: string;
  /** Nom de la période dans la légende (défaut « Période »), ex. « CA TTC ». */
  currentLabel?: string;
  /** Un point sur chaque jour de la période, quel que soit leur nombre (défaut : jusqu'à 12). */
  dots?: boolean;
}

/** Libellé d'un seau : un jour, ou le premier jour d'une tranche. */
function bucketLabel(date: string, bucketSize: number): string {
  if (bucketSize === 1) return formatDayLong(date);
  return `${bucketSize === 7 ? 'Semaine' : '30 jours'} à partir du ${formatDayLong(date)}`;
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: ComparedPoint }>;
  bucketSize: number;
  referenceLabel: string;
  currentLabel: string;
  formatValue: (value: number) => string;
}

/**
 * Infobulle dédiée : l'infobulle commune ne sait afficher qu'un en-tête de
 * date, et la référence a la sienne.
 */
function ComparedTooltip({ active, payload, bucketSize, referenceLabel, currentLabel, formatValue }: TooltipProps) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;

  const change = point.reference !== null ? relativeChange(point.current, point.reference) : null;

  return (
    <div className="tooltip" role="tooltip">
      <p className="tooltip__title">{bucketLabel(point.date, bucketSize)}</p>
      <div className="tooltip__row">
        <span className="legend__key" style={{ background: COLORS.series1 }} aria-hidden="true" />
        <span className="tooltip__name">{currentLabel}</span>
        <span className="tooltip__value">{formatValue(point.current)}</span>
      </div>
      {point.reference !== null && (
        <div className="tooltip__row">
          <span className="legend__key" style={{ background: COLORS.series2 }} aria-hidden="true" />
          <span className="tooltip__name">
            {referenceLabel}
            {point.referenceDate ? ` (${formatDayShort(point.referenceDate)})` : ''}
          </span>
          <span className="tooltip__value">{formatValue(point.reference)}</span>
        </div>
      )}
      {change !== null && (
        <p className="tooltip__title">
          {change >= 0 ? '+' : '−'}
          {Math.abs(change).toLocaleString('fr-FR')}&nbsp;%
        </p>
      )}
    </div>
  );
}


/** Marqueurs « ventes » : l'impact se lit sur la courbe de la période. */
const MARKER_CONFIG = {
  value: (point: ComparedPoint) => point.current,
  mode: 'level',
  unit: 'count',
  higherIsBetter: true,
  scope: 'sales',
} as const;

export function SalesTrendChart<T extends { date: string }>({
  title,
  subtitle,
  current,
  reference,
  referenceLabel,
  value,
  formatValue,
  formatAxis,
  aggregate = 'sum',
  className,
  currentLabel = 'Période',
  dots = false,
}: SalesTrendChartProps<T>) {
  const bucketSize = bucketSizeFor(current.length);
  // Sans référence (une seule courbe) : ni pointillé, ni colonnes d'écart.
  const withReference = reference.length > 0;

  const points = useMemo(
    () => compareSeries(current, reference, value, bucketSize, aggregate),
    [current, reference, value, bucketSize, aggregate],
  );
  const markers = useChartMarkers(points, MARKER_CONFIG);

  const allColumns: ReadonlyArray<DataColumn<ComparedPoint>> = [
    { key: 'date', header: bucketSize === 1 ? 'Jour' : 'À partir du', render: (row) => formatDayLong(row.date) },
    { key: 'current', header: currentLabel, numeric: true, render: (row) => formatValue(row.current) },
    {
      key: 'reference',
      header: referenceLabel,
      numeric: true,
      render: (row) => (row.reference !== null ? formatValue(row.reference) : '—'),
    },
    {
      key: 'change',
      header: 'Écart',
      numeric: true,
      render: (row) => {
        const change = row.reference !== null ? relativeChange(row.current, row.reference) : null;
        return change === null ? '—' : `${change >= 0 ? '+' : '−'}${Math.abs(change).toLocaleString('fr-FR')} %`;
      },
    },
  ];
  const columns = withReference ? allColumns : allColumns.filter((column) => column.key === 'date' || column.key === 'current');

  const granularity =
    bucketSize === 1
      ? 'par jour'
      : aggregate === 'last'
        ? `au dernier jour de chaque ${bucketSize === 7 ? 'semaine' : 'tranche de 30 jours'}`
        : bucketSize === 7
          ? 'par semaine'
          : 'par tranche de 30 jours';

  return (
    <ChartCard
      footer={<MarkerStrip items={markers.items} />}
      title={title}
      subtitle={`${subtitle}, ${granularity}`}
      className={className}
      rows={points}
      columns={columns}
      emptyMessage="Aucune vente sur cette période."
      chartHeight={290}
    >
      <ChartLegend
        entries={[
          { label: currentLabel, color: COLORS.series1 },
          ...(withReference ? [{ label: referenceLabel, color: COLORS.series2, shape: 'dashed' as const }] : []),
        ]}
      />
      <ResponsiveContainer width="100%" height="86%">
        <LineChart data={points} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={formatDayShort}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
            minTickGap={40}
          />
          <YAxis
            tickFormatter={formatAxis}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={60}
          />
          <Tooltip
            cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }}
            content={
              <ComparedTooltip
                bucketSize={bucketSize}
                referenceLabel={referenceLabel}
                currentLabel={currentLabel}
                formatValue={formatValue}
              />
            }
          />
          {/* Référence d'abord : la période se dessine par-dessus. */}
          {markers.lines}
          {withReference && (
          <Line
            isAnimationActive={false}
            type="monotone"
            dataKey="reference"
            name={referenceLabel}
            stroke={COLORS.series2}
            strokeWidth={LINE_WIDTH}
            strokeDasharray="5 4"
            dot={false}
            activeDot={{ r: 3, fill: COLORS.series2, stroke: COLORS.surface, strokeWidth: 2 }}
            connectNulls={false}
          />
          )}
          <Line
            isAnimationActive={false}
            type="monotone"
            dataKey="current"
            name={currentLabel}
            stroke={COLORS.series1}
            strokeWidth={LINE_WIDTH}
            dot={
              dots
                ? { r: 4, fill: COLORS.series1, stroke: COLORS.surface, strokeWidth: 2 }
                : points.length <= 12
                  ? { r: 3, fill: COLORS.series1, strokeWidth: 0 }
                  : false
            }
            activeDot={{ r: 4, fill: COLORS.series1, stroke: COLORS.surface, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
